import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { X, Package, AlertCircle } from "lucide-react";
import { BulkOrderEntry } from "@/hooks/useBulkOrderProducts";

interface BulkOrderCardProps {
  entry: BulkOrderEntry;
  onMessageChange: (voNumber: string, message: string) => void;
  onRemove: (voNumber: string) => void;
}

const BulkOrderCard = ({ entry, onMessageChange, onRemove }: BulkOrderCardProps) => {
  if (entry.isLoading) {
    return (
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Badge variant="outline">{entry.voNumber}</Badge>
          <Skeleton className="h-4 w-20" />
        </div>
        <Skeleton className="h-10 w-full" />
      </Card>
    );
  }

  if (entry.error) {
    return (
      <Card className="p-3 border-destructive/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="destructive">{entry.voNumber}</Badge>
            <span className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {entry.error}
            </span>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(entry.voNumber)}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="font-mono">{entry.voNumber}</Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Package className="h-3 w-3" /> {entry.products.length} product(en)
          </span>
          {entry.type && <Badge variant="outline" className="text-xs">{entry.type}</Badge>}
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(entry.voNumber)}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Product thumbnails */}
      {entry.products.length > 0 && (
        <div className="flex items-center gap-1.5">
          {entry.products.slice(0, 3).map((p, i) => (
            <div key={i} className="h-10 w-10 rounded border bg-muted overflow-hidden flex-shrink-0">
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={p.productTitle} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-[8px] text-muted-foreground">
                  {p.sku.slice(0, 4)}
                </div>
              )}
            </div>
          ))}
          {entry.products.length > 3 && (
            <span className="text-xs text-muted-foreground">+{entry.products.length - 3} meer</span>
          )}
        </div>
      )}

      <Textarea
        value={entry.message}
        onChange={(e) => onMessageChange(entry.voNumber, e.target.value)}
        placeholder="Bericht (optioneel aanpassen)"
        className="min-h-[40px] text-sm resize-none"
        rows={1}
      />
    </Card>
  );
};

export default BulkOrderCard;
